import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import clsx from "clsx";
import { FC, Fragment, HTMLAttributes, ReactNode, useState } from "react";

import { GroupedMenuOption } from "@dndbeyond/character-rules-engine";
import ChevronDown from "@dndbeyond/fontawesome-cache/svgs/solid/chevron-down.svg";

import { Button, ButtonProps } from "~/components/Button";

import styles from "./styles.module.css";

/**
 * A button that displays a menu of options when clicked. If there is only one option, it will
 * render as a simple button that triggers the onSelect callback when clicked unless the
 * `showSingleOption` prop is set to true, in which case it will still show the dropdown with only
 * one option.
 */

export interface ButtonWithMenuProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onSelect"> {
  placement?: Placement;
  groupedOptions: Array<GroupedMenuOption>;
  showSingleOption?: boolean;
  onSelect: (containerDefinitionKey: string) => void;
  variant?: ButtonProps["variant"];
}

export const ButtonWithMenu: FC<ButtonWithMenuProps> = ({
  className,
  children,
  groupedOptions,
  showSingleOption,
  placement,
  onSelect,
  variant,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Set up floating element and trigger
  const { refs, floatingStyles, context } = useFloating({
    placement,
    strategy: "fixed",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });
  // Handle interactions for the floating element
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context),
  ]);
  // Determine whether we should show the dropdown
  const hasSingleOption =
    groupedOptions.length === 1 &&
    groupedOptions[0].options?.length === 1 &&
    !showSingleOption;

  const handleClickTrigger = (e) => {
    e.stopPropagation();

    if (hasSingleOption) {
      onSelect(groupedOptions[0].options[0]?.value);
    } else {
      setIsOpen((prev) => !prev);
    }
  };

  const handleClickOption = (e, value: string) => {
    e.stopPropagation();
    onSelect(value);
    setIsOpen(false);
  };

  const getTestId = (value: ReactNode) => {
    const content = value?.toString() ?? "Move";
    return `theme-button-with-menu-${content}`
      .toLowerCase()
      .replace(/\s/g, "-");
  };

  return (
    <div className={clsx([styles.buttonWithMenu, className])} {...props}>
      <span
        className={styles.trigger}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <Button
          size="xx-small"
          variant={variant}
          themed
          data-testid={getTestId(children)}
          onClick={handleClickTrigger}
        >
          {children}
          {!hasSingleOption && <ChevronDown className={styles.buttonIcon} />}
        </Button>
      </span>
      {!hasSingleOption && isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <menu
            className={styles.menu}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {groupedOptions.map((option) => (
              <Fragment key={option.label}>
                <li
                  className={styles.menuHeading}
                  data-testid={getTestId(`subheader-${option.label}`)}
                >
                  {option.label}
                </li>
                <ul>
                  {option.options?.map((item) => (
                    <li key={item.value}>
                      <button
                        className={styles.menuButton}
                        onClick={(e) => handleClickOption(e, item.value)}
                        data-testid={getTestId(`item-${item.label}`)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </Fragment>
            ))}
          </menu>
        </FloatingFocusManager>
      )}
    </div>
  );
};
